import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { usePlan } from "@/context/PlanContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import AuthGate from "@/components/AuthGate";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { User, Crown, Check, X, Loader2, BarChart3, Star, KeyRound, ShieldCheck, ChevronRight, LogOut, Scale } from "lucide-react";

function AccountInner() {
  const { user, setUser, logout } = useAuth();
  const { pro, plan } = usePlan();
  const [username, setUsername] = useState(user.username || "");
  const [avail, setAvail] = useState(null);
  const [saving, setSaving] = useState(false);
  const dirty = username !== (user.username || "");

  useEffect(() => {
    if (!dirty || username.length < 3) { setAvail(null); return; }
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/auth/username-available", { params: { u: username } }); setAvail(data); }
      catch { setAvail(null); }
    }, 350);
    return () => clearTimeout(t);
  }, [username, dirty]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/auth/me", { username });
      setUser(data); setAvail(null);
      toast.success(`You're now @${data.username}`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-8" data-testid="account-page">
      <h1 className="font-display text-3xl font-bold flex items-center gap-2"><User className="h-7 w-7 text-emerald-400" /> Account</h1>
      <p className="text-slate-400 text-sm mt-1 mb-6">Your username is what shows on the site and on share cards — your email and real name stay private.</p>

      <Card className="glass border-[#222F43] p-5 mb-4">
        <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Username</label>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="text-slate-500 font-mono text-lg">@</span>
          <Input value={username} onChange={(e) => setUsername(e.target.value.replace(/[^A-Za-z0-9_]/g, "").slice(0, 20))}
            className="bg-[#0d1420] border-[#334566] font-mono" data-testid="account-username-input" />
          <Button onClick={save} disabled={!dirty || saving || (avail && !avail.available) || username.length < 3}
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="account-username-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </Button>
        </div>
        <div className="text-xs mt-2 font-mono h-4" data-testid="account-username-status">
          {dirty && username.length < 3 && <span className="text-slate-500">3–20 characters: letters, numbers, underscore</span>}
          {avail?.available && <span className="text-emerald-400 flex items-center gap-1"><Check className="h-3 w-3" /> available</span>}
          {avail && !avail.available && <span className="text-red-400 flex items-center gap-1"><X className="h-3 w-3" /> {avail.reason}</span>}
        </div>
      </Card>

      <Card className="glass border-[#222F43] p-5 mb-4">
        <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Email (private)</label>
        <div className="font-mono text-sm mt-1.5 text-slate-300" data-testid="account-email">{user.email}</div>
        {user.name && user.name !== user.username && <div className="text-xs text-slate-500 mt-1">Name on file: {user.name} — never shown publicly</div>}
      </Card>

      <Card className="glass border-[#222F43] p-5 mb-4">
        <label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Plan</label>
        <div className="flex items-center justify-between mt-1.5">
          <div className="flex items-center gap-2 text-sm" data-testid="account-plan">
            <Crown className={`h-4 w-4 ${pro ? "text-amber-400" : "text-slate-600"}`} />
            {pro ? (plan?.source === "lifetime" ? "Pro · Lifetime" : plan?.source === "admin" ? "Admin" : plan?.source === "trial" ? "Pro · Trial" : "Pro") : "Free"}
          </div>
          <Link to="/pricing" className="text-xs text-emerald-400 hover:underline" data-testid="account-plan-link">{pro ? "Manage plan" : "Upgrade to Pro"}</Link>
        </div>
      </Card>

      {/* Mobile-only shortcuts (desktop has these in the top nav / avatar menu) */}
      <div className="md:hidden rounded-xl border border-[#222F43] bg-[#111723] overflow-hidden" data-testid="account-mobile-links">
        {[
          ["/portfolio", "My Portfolio", BarChart3],
          ["/watchlist", "Watchlist", Star],
          ["/pricing", pro ? "My plan" : "Upgrade to Pro", Crown],
          ...(user.role === "admin" ? [["/admin/codes", "Lifetime codes", KeyRound], ["/admin/team", "Admins", ShieldCheck]] : []),
          ["/legal", "Terms, privacy & disclaimer", Scale],
        ].map(([to, label, Icon]) => (
          <Link key={to} to={to} className="tap flex items-center gap-3 px-4 py-3.5 border-b border-[#222F43] text-sm text-slate-200 last:border-0" data-testid={`account-link-${to.replace(/\//g, "-").slice(1)}`}>
            <Icon className="h-4 w-4 text-emerald-400" /> <span className="flex-1">{label}</span> <ChevronRight className="h-4 w-4 text-slate-600" />
          </Link>
        ))}
        <button onClick={logout} className="tap w-full flex items-center gap-3 px-4 py-3.5 text-sm text-red-400" data-testid="account-logout">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </div>
    </div>
  );
}

export default function Account() {
  return <AuthGate><AccountInner /></AuthGate>;
}
